import { TemplateRef } from '@angular/core';

export interface WsTooltipRow {
  label: string;
  value: string;
}

export interface WsTooltipTitle {
  text: string;
  iconUrl?: string;
  iconTemplate?: TemplateRef<any>;
}

export interface WsTooltipTemplateContext {
  $implicit?: any;
  [key: string]: any;
}

export interface WsTooltipListContent {
  variant: 'list';
  rows: WsTooltipRow[];
}

export interface WsTooltipDetailedContent {
  variant: 'detailed';
  title: string | WsTooltipTitle;
  rows: WsTooltipRow[];
}

export interface WsTooltipTemplateContent {
  variant: 'template';
  template: TemplateRef<any>;
  context?: WsTooltipTemplateContext;
}

export type WsTooltipContent =
  | string
  | number
  | TemplateRef<any>
  | WsTooltipListContent
  | WsTooltipDetailedContent
  | WsTooltipTemplateContent;

export type WsTooltipPlacement = 'above' | 'below';

export type WsTooltipContentType = 'text' | 'list' | 'detailed' | 'template';

export interface WsTooltipParsedContent {
  type: WsTooltipContentType;
  text?: string;
  titleText?: string;
  titleIconUrl?: string;
  titleIconTemplate?: TemplateRef<any>;
  rows?: WsTooltipRow[];
  template?: TemplateRef<any>;
  templateContext?: WsTooltipTemplateContext;
}

function isTemplateRef(value: unknown): value is TemplateRef<any> {
  return !!value && typeof value === 'object' && 'createEmbeddedView' in (value as TemplateRef<any>);
}

export function parseWsTooltipContent(
  content: WsTooltipContent | null | undefined,
  context?: WsTooltipTemplateContext | null
): WsTooltipParsedContent | null {
  if (content == null || content === '') {
    return null;
  }

  if (typeof content === 'number') {
    return { type: 'text', text: String(content) };
  }

  if (typeof content === 'string') {
    return { type: 'text', text: content };
  }

  if (isTemplateRef(content)) {
    return {
      type: 'template',
      template: content,
      templateContext: context || {},
    };
  }

  if (content.variant === 'template') {
    return {
      type: 'template',
      template: content.template,
      templateContext: {
        ...(content.context || {}),
        ...(context || {}),
      },
    };
  }

  if (content.variant === 'list') {
    return { type: 'list', rows: content.rows || [] };
  }

  const title = content.title;
  if (typeof title === 'string') {
    return {
      type: 'detailed',
      titleText: title,
      rows: content.rows || [],
    };
  }

  return {
    type: 'detailed',
    titleText: title.text,
    titleIconUrl: title.iconUrl,
    titleIconTemplate: title.iconTemplate,
    rows: content.rows || [],
  };
}

export function isWsTooltipContentEmpty(
  content: WsTooltipContent | null | undefined
): boolean {
  if (content == null || content === '') {
    return true;
  }

  if (isTemplateRef(content)) {
    return false;
  }

  if (typeof content === 'object' && content.variant === 'template') {
    return !content.template;
  }

  const parsed = parseWsTooltipContent(content);
  if (!parsed) {
    return true;
  }

  if (parsed.type === 'text') {
    return !parsed.text?.trim();
  }

  if (parsed.type === 'list') {
    return !parsed.rows?.length;
  }

  if (parsed.type === 'template') {
    return !parsed.template;
  }

  return !parsed.titleText?.trim() && !parsed.rows?.length;
}
